/** Versioned wire contract; mirrored in sparkles-extensions/functions/src/ai-photo-contract.ts. */
import { z } from 'zod';

export const AI_PHOTO_EXTENSION_ID = 'ai-photo-tool';
export const AI_PHOTO_COLLECTION = 'aiPhotoCatalogs';
export const AI_PHOTO_PUBLISHED_COLLECTION = 'aiPhotoPublished';
export const AI_PHOTO_SHOPS = ['hippe', 'mfm', 'po-fr', 'po-de'] as const;
export const AI_PHOTO_ENVIRONMENTS = ['acceptance', 'production'] as const;
export const DEFAULT_AI_PHOTO_CONFIG_ID = 'default';
/** Public, immutable selection ID. No double hyphens/path separators: safe document namespace. */
export const AiPhotoConfigIdSchema = z.string().min(2).max(64).regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/);
export function normalizeAiPhotoConfigId(value: unknown = DEFAULT_AI_PHOTO_CONFIG_ID): string {
  return AiPhotoConfigIdSchema.parse(value);
}
export const AiPhotoShopSchema = z.enum(AI_PHOTO_SHOPS);
export const AiPhotoEnvironmentSchema = z.enum(AI_PHOTO_ENVIRONMENTS);
export type AiPhotoShopId = z.infer<typeof AiPhotoShopSchema>;
export type AiPhotoEnvironmentId = z.infer<typeof AiPhotoEnvironmentSchema>;

const ShortText = z.string().trim().min(1).max(120);
export const AiPhotoLookIdSchema = z.string().regex(/^[a-z][a-z0-9-]{1,63}$/)
  .refine((id) => id !== 'stijlen' && id !== 'scenes', 'Dit id is gereserveerd voor een keuzescherm.');

export function isAllowedExampleUrl(value: string): boolean {
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.username || url.password || url.hash) return false;
    return (url.hostname === 'firebasestorage.googleapis.com' &&
      /^\/v0\/b\/crystalminer(?:\.firebasestorage\.app|\.appspot\.com)\/o\//.test(url.pathname)) ||
      (url.hostname === 'storage.googleapis.com' &&
        /^\/crystalminer(?:\.firebasestorage\.app|\.appspot\.com)\//.test(url.pathname)) ||
      (url.hostname === 'ai-photo-style.web.app' && url.pathname.startsWith('/examples/'));
  } catch {
    return false;
  }
}

export const AiPhotoLookSchema = z.object({
  id: AiPhotoLookIdSchema,
  kind: z.enum(['style', 'scene']),
  label: ShortText,
  description: z.string().trim().max(500),
  /** Complete effective prompt, never included in the public catalog. */
  prompt: z.string().trim().min(10).max(12000),
  enabled: z.boolean(),
  order: z.number().int().min(0).max(10000),
  groups: z.array(z.enum(['stijlen', 'scenes'])).min(1).max(2)
    .refine((groups) => new Set(groups).size === groups.length, 'Dubbele groep.'),
  previewUrl: z.string().max(2048).refine(isAllowedExampleUrl, 'Gebruik een voorbeeld uit de toegestane assetopslag.').nullable(),
  /** Optional alternate presentation for legacy family-watercolor in the scenes chooser. */
  sceneLabel: ShortText.optional(),
  sceneDescription: z.string().trim().max(500).optional(),
  output: z.object({
    format: z.enum(['png', 'jpeg']),
    background: z.enum(['transparent', 'opaque']),
    /** Absent keeps the photo's shape. Square forces a 1:1 file. */
    aspect: z.enum(['match', 'square']).optional(),
  }).strict().refine((output) => output.background !== 'transparent' || output.format === 'png',
    'Transparantie vereist PNG.'),
}).strict();
export type AiPhotoLook = z.infer<typeof AiPhotoLookSchema>;

export const AiPhotoConfigSchema = z.object({
  schemaVersion: z.literal(1),
  /** Optional for existing v1 drafts. Renaming never changes configId. */
  name: ShortText.optional(),
  /** Square image on the product-page button. Absent keeps the built-in illustration. */
  ctaImageUrl: z.string().max(2048).refine(isAllowedExampleUrl, 'Gebruik een afbeelding uit de toegestane assetopslag.').nullable().optional(),
  enabled: z.boolean(),
  looks: z.array(AiPhotoLookSchema).max(40),
}).strict().superRefine((config, ctx) => {
  const ids = new Set<string>();
  for (const [index, look] of config.looks.entries()) {
    if (ids.has(look.id)) ctx.addIssue({ code: 'custom', path: ['looks', index, 'id'], message: 'Dit stijl-id bestaat al.' });
    ids.add(look.id);
  }
  if (config.enabled && !config.looks.some((look) => look.enabled)) {
    ctx.addIssue({ code: 'custom', path: ['looks'], message: 'Een actieve catalogus heeft minstens één actieve stijl nodig.' });
  }
  if (new TextEncoder().encode(JSON.stringify(config)).byteLength > 600000) {
    ctx.addIssue({ code: 'custom', message: 'De catalogus is te groot.' });
  }
});
export type AiPhotoConfig = z.infer<typeof AiPhotoConfigSchema>;

export const AiPhotoSnapshotSchema = z.object({
  schemaVersion: z.literal(1),
  shopId: AiPhotoShopSchema,
  environmentId: AiPhotoEnvironmentSchema,
  configId: AiPhotoConfigIdSchema.optional(),
  revision: z.string().regex(/^[a-zA-Z0-9._-]{1,100}$/),
  config: AiPhotoConfigSchema,
  publishedAt: z.string().datetime(),
  publishedBy: z.string().min(1).max(254),
}).strict();
export type AiPhotoSnapshot = z.infer<typeof AiPhotoSnapshotSchema>;

export const PublicAiPhotoLookSchema = AiPhotoLookSchema.omit({ prompt: true, output: true });
export type PublicAiPhotoLook = z.infer<typeof PublicAiPhotoLookSchema>;
export const PublicAiPhotoCatalogSchema = z.object({
  schemaVersion: z.literal(1),
  shopId: AiPhotoShopSchema,
  environmentId: AiPhotoEnvironmentSchema,
  configId: AiPhotoConfigIdSchema.optional(),
  name: ShortText.optional(),
  ctaImageUrl: z.string().max(2048).refine(isAllowedExampleUrl, 'Gebruik een afbeelding uit de toegestane assetopslag.').nullable().optional(),
  revision: z.string().min(1).max(100),
  enabled: z.boolean(),
  looks: z.array(PublicAiPhotoLookSchema).max(40),
}).strict();
export type PublicAiPhotoCatalog = z.infer<typeof PublicAiPhotoCatalogSchema>;

export function publicAiPhotoCatalog(snapshot: AiPhotoSnapshot): PublicAiPhotoCatalog {
  return PublicAiPhotoCatalogSchema.parse({
    schemaVersion: 1,
    shopId: snapshot.shopId,
    environmentId: snapshot.environmentId,
    // Keep the legacy default DTO byte-shape compatible with already-loaded strict readers.
    ...(normalizeAiPhotoConfigId(snapshot.configId) !== DEFAULT_AI_PHOTO_CONFIG_ID ? {
      configId: snapshot.configId,
      ...(snapshot.config.name ? { name: snapshot.config.name } : {}),
    } : {}),
    ...(snapshot.config.ctaImageUrl ? { ctaImageUrl: snapshot.config.ctaImageUrl } : {}),
    revision: snapshot.revision,
    enabled: snapshot.config.enabled,
    looks: snapshot.config.looks.map(({ prompt: _prompt, output: _output, ...look }) => look),
  });
}

export function aiPhotoDocumentId(shopId: AiPhotoShopId, environmentId: AiPhotoEnvironmentId, configId?: string): string {
  const id = normalizeAiPhotoConfigId(configId);
  return `${shopId}--${environmentId}${id === DEFAULT_AI_PHOTO_CONFIG_ID ? '' : `--${id}`}`;
}

export interface AiPhotoDraft {
  config: AiPhotoConfig;
  version: number;
  updatedAt: string;
  updatedBy: string;
}
export interface AiPhotoVersionSummary {
  revision: string;
  publishedAt: string;
  publishedBy: string;
  action: 'publish' | 'rollback';
  restoredFrom?: string;
}
export interface AiPhotoAuditEntry {
  id: string;
  action: 'save' | 'publish' | 'rollback' | 'preview';
  at: string;
  actor: string;
  revision?: string;
}
export interface AiPhotoAdminState {
  shopId: AiPhotoShopId;
  environmentId: AiPhotoEnvironmentId;
  configId?: string;
  draft: AiPhotoDraft;
  published: AiPhotoSnapshot | null;
  versions: AiPhotoVersionSummary[];
  audit: AiPhotoAuditEntry[];
}
export interface AiPhotoScope {
  shopId: AiPhotoShopId;
  environmentId: AiPhotoEnvironmentId;
  /** Missing means the existing default configuration, never an arbitrary named config. */
  configId?: string;
}
export interface AiPhotoConfigSummary {
  configId: string;
  name: string;
  enabled: boolean;
  draftVersion: number;
  publishedRevision: string | null;
}
export interface AiPhotoConfigList {
  shopId: AiPhotoShopId;
  environmentId: AiPhotoEnvironmentId;
  configs: AiPhotoConfigSummary[];
}
export interface AiPhotoCreateConfigInput extends AiPhotoScope {
  configId: string;
  name: string;
  sourceConfigId?: string;
  expectedSourceDraftVersion?: number;
}
export interface AiPhotoPreviewResult {
  imageBase64: string;
  mime: 'image/png' | 'image/jpeg';
  lookId: string;
  draftVersion: number;
  configId?: string;
}

export const AI_PHOTO_FUNCTIONS = {
  list: 'listAiPhotoConfigs',
  create: 'createAiPhotoConfig',
  get: 'getAiPhotoCatalog',
  save: 'saveAiPhotoCatalogDraft',
  publish: 'publishAiPhotoCatalog',
  rollback: 'rollbackAiPhotoCatalog',
  preview: 'previewAiPhotoLook',
} as const;